/**
 * SumUpReconciliationPanel — „SumUp-Abgleich"-Übersicht (Jul 2026, User-Wunsch).
 *
 * Zeigt: 1) automatisch verbuchte Zahlungen (Terminal / Nightly-Sync / manuell),
 * 2) noch nicht zugeordnete erfolgreiche SumUp-Transaktionen mit 1-Klick-Zuordnung
 * auf offene EC-Rechnungen, 3) „Jetzt abgleichen" (Sync heute + Auto-Matching).
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, HelpCircle, Link2, Search as SearchIcon } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtDt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};
const SOURCE_LABEL = {
    sumup_terminal: { label: "Terminal", cls: "bg-blue-100 text-blue-800" },
    sumup_nightly_sync: { label: "Nightly-Sync", cls: "bg-violet-100 text-violet-800" },
    sumup_manual_match: { label: "Manuell", cls: "bg-gray-200 text-gray-700" },
};

export default function SumUpReconciliationPanel() {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [assignSel, setAssignSel] = useState({}); // {tx_code: invoice_id}
    const [assignNo, setAssignNo] = useState({}); // {tx_code: Suchtext/Belegnummer}
    const [assigning, setAssigning] = useState(null);
    // Aug-2026 (User-Wunsch): Universalsuche — Ergebnisse pro Transaktion
    const [searchRes, setSearchRes] = useState({}); // {tx_code: {items, searched}}
    const [searchBusy, setSearchBusy] = useState(null);

    const runSearch = async (code, qOverride) => {
        const q = String(qOverride ?? assignNo[code] ?? "").trim();
        if (q.length < 2) { toast.error("Mindestens 2 Zeichen eingeben (Betrag, Nummer, Kunde oder Datum)"); return; }
        setSearchBusy(code);
        try {
            const r = await api.get(`/buchhaltung/ec/invoice-search?q=${encodeURIComponent(q)}`);
            setSearchRes((p) => ({ ...p, [code]: { items: r.data?.items || [], searched: q } }));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Suche fehlgeschlagen");
        }
        setSearchBusy(null);
    };

    // Aug-2026 (User-Wunsch): Belegnummer zuordnen, die noch nicht im System ist
    const assignExternal = async (code) => {
        const ref = (assignNo[code] || "").trim();
        if (!ref) return;
        setAssigning(code);
        try {
            await api.post(`/buchhaltung/ec/sumup/transactions/${encodeURIComponent(code)}/assign`, { external_reference: ref });
            toast.success(`Zahlung unter Beleg-Nr. „${ref}" vorgemerkt (Beleg noch nicht im System)`);
            setAssignNo((p) => ({ ...p, [code]: "" }));
            setSearchRes((p) => ({ ...p, [code]: undefined }));
            load(days);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
        }
        setAssigning(null);
    };

    const assignById = async (code, invoiceId) => {
        setAssigning(code);
        try {
            const r = await api.post(`/buchhaltung/ec/sumup/transactions/${encodeURIComponent(code)}/assign`, { invoice_id: invoiceId });
            toast.success(`Rechnung ${r.data?.invoice_number || ""} → ${r.data?.new_status === "paid" ? "bezahlt" : "teilbezahlt"}`);
            setSearchRes((p) => ({ ...p, [code]: undefined }));
            setAssignNo((p) => ({ ...p, [code]: "" }));
            load(days);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
        }
        setAssigning(null);
    };

    const load = useCallback(async (d = days) => {
        setLoading(true);
        try {
            const r = await api.get(`/buchhaltung/ec/sumup/reconciliation?days=${d}`);
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Abgleich konnte nicht geladen werden");
        }
        setLoading(false);
    }, [days]);

    useEffect(() => { load(days); }, [days, load]);

    const syncNow = async () => {
        setSyncing(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            // Jul-2026 (Bugfix): kompletten angezeigten Zeitraum abgleichen —
            // nicht nur heute (sonst fehlen Zahlungen vergangener Tage).
            const r = await api.post("/buchhaltung/ec/providers/sumup/sync", { date: today, days });
            const n = r.data?.auto_booked || 0;
            toast.success(n > 0
                ? `Abgleich (${days} Tage) fertig — ${n} Rechnung(en) automatisch als bezahlt verbucht`
                : `Abgleich (${days} Tage) fertig — keine neuen automatischen Buchungen`);
            load(days);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync fehlgeschlagen");
        }
        setSyncing(false);
    };

    const assign = async (code) => {
        const invId = assignSel[code];
        if (!invId) { toast.error("Bitte Rechnung wählen oder die Suche nutzen"); return; }
        setAssigning(code);
        try {
            const r = await api.post(`/buchhaltung/ec/sumup/transactions/${encodeURIComponent(code)}/assign`, { invoice_id: invId });
            toast.success(`Rechnung ${r.data?.invoice_number || ""} → ${r.data?.new_status === "paid" ? "bezahlt" : "teilbezahlt"}`);
            setAssignNo((p) => ({ ...p, [code]: "" }));
            load(days);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
        }
        setAssigning(null);
    };

    const booked = data?.auto_booked || [];
    const unmatched = data?.unmatched_transactions || [];
    const openInvs = data?.open_ec_invoices || [];

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mt-4" data-testid="sumup-reconciliation-panel">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <h3 className="font-bold text-base">SumUp-Abgleich</h3>
                <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-sm font-bold">Auto-Bezahlt</span>
                <div className="ml-auto flex items-center gap-1.5">
                    {[7, 30].map((d) => (
                        <button key={d} type="button" onClick={() => setDays(d)}
                            className={`text-[11px] font-bold px-2 py-1 rounded-sm border ${days === d ? "bg-[#111827] text-white border-[#111827]" : "text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                            data-testid={`sumup-recon-days-${d}`}>
                            {d} Tage
                        </button>
                    ))}
                    <Button type="button" onClick={syncNow} disabled={syncing}
                        className="h-7 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-[#E6B800] text-[11px] font-black"
                        data-testid="sumup-recon-sync-btn">
                        {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Jetzt abgleichen
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-xs text-gray-500 inline-flex items-center gap-1.5 py-2"><Loader2 className="w-3 h-3 animate-spin" /> Lade Abgleich…</div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Automatisch verbucht */}
                    <div className="border border-gray-200 rounded-sm p-3">
                        <div className="font-bold text-sm mb-2 flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Automatisch verbucht
                            <span className="text-[10px] text-gray-400 font-normal">({booked.length})</span>
                        </div>
                        {booked.length === 0 ? (
                            <div className="text-xs text-gray-500" data-testid="sumup-recon-booked-empty">Keine automatischen Buchungen in den letzten {days} Tagen.</div>
                        ) : (
                            <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="sumup-recon-booked-list">
                                {booked.map((p) => {
                                    const s = SOURCE_LABEL[p.source] || { label: p.source, cls: "bg-gray-100 text-gray-600" };
                                    return (
                                        <div key={p.id} className="flex items-center justify-between text-xs border border-gray-100 rounded-sm px-2 py-1.5" data-testid={`sumup-recon-booked-${p.id}`}>
                                            <div className="min-w-0">
                                                {/* Jul-2026 (User-Wunsch): Beleg anklickbar → Rechnungsliste gefiltert öffnen */}
                                                <Link to={`/dealer/invoices?q=${encodeURIComponent(p.invoice_number || "")}`}
                                                    className="font-bold text-[#111827] underline underline-offset-2 decoration-gray-300 hover:decoration-[#FFCC00] hover:text-black"
                                                    data-testid={`sumup-recon-booked-link-${p.id}`}>
                                                    {p.invoice_number || "—"}
                                                </Link>
                                                <span className="text-gray-400 ml-2">{fmtDt(p.created_at)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${s.cls}`}>{s.label}</span>
                                                <span className="font-mono-num font-bold text-emerald-700">{fmtEur(p.amount)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Nicht zugeordnet — manuelle Zuordnung */}
                    <div className="border border-gray-200 rounded-sm p-3">
                        <div className="font-bold text-sm mb-2 flex items-center gap-1.5">
                            <HelpCircle className="w-3.5 h-3.5 text-amber-600" /> Nicht zugeordnete Transaktionen
                            <span className="text-[10px] text-gray-400 font-normal">({unmatched.length})</span>
                        </div>
                        {unmatched.length === 0 ? (
                            <div className="text-xs text-gray-500" data-testid="sumup-recon-unmatched-empty">Alle synchronisierten SumUp-Zahlungen sind zugeordnet. ✓</div>
                        ) : (
                            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="sumup-recon-unmatched-list">
                                {unmatched.map((t) => (
                                    <div key={t.transaction_code} className="border border-amber-200 bg-amber-50/50 rounded-sm p-2" data-testid={`sumup-recon-tx-${t.transaction_code}`}>
                                        <div className="flex items-center justify-between text-xs mb-1.5">
                                            <span className="text-gray-600">{fmtDt(t.timestamp)}{t.card_type ? ` · ${t.card_type}` : ""}{t.site_label ? <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600"> · {t.site_label}</span> : null}</span>
                                            <span className="font-mono-num font-bold">{fmtEur(t.amount)}</span>
                                        </div>
                                        <div className="flex gap-1.5 flex-wrap">
                                            <select
                                                value={assignSel[t.transaction_code] || ""}
                                                onChange={(e) => setAssignSel((p) => ({ ...p, [t.transaction_code]: e.target.value }))}
                                                className="flex-1 border border-gray-300 rounded-sm px-1.5 py-1 text-xs bg-white min-w-0"
                                                data-testid={`sumup-recon-select-${t.transaction_code}`}>
                                                <option value="">Offene EC-Rechnung wählen…</option>
                                                {openInvs.map((inv) => (
                                                    <option key={inv.id} value={inv.id}>
                                                        {inv.invoice_number} · offen {fmtEur(inv.remaining)}{Math.abs(inv.remaining - t.amount) < 0.01 ? " ✓ Betrag passt" : ""}
                                                    </option>
                                                ))}
                                            </select>
                                            <Button type="button" size="sm" onClick={() => assign(t.transaction_code)}
                                                disabled={assigning === t.transaction_code || !assignSel[t.transaction_code]}
                                                className="h-7 rounded-sm bg-[#111827] text-white hover:bg-black text-[11px] font-bold shrink-0"
                                                data-testid={`sumup-recon-assign-${t.transaction_code}`}>
                                                {assigning === t.transaction_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3 mr-1" />}
                                                Zuordnen
                                            </Button>
                                        </div>
                                        {/* Aug-2026 (User-Wunsch): Universalsuche — Betrag, RG-/Auftragsnr., Kunde, Datum */}
                                        <div className="flex gap-1.5 mt-1.5">
                                            <input
                                                type="text"
                                                value={assignNo[t.transaction_code] || ""}
                                                onChange={(e) => setAssignNo((p) => ({ ...p, [t.transaction_code]: e.target.value }))}
                                                onKeyDown={(e) => { if (e.key === "Enter") runSearch(t.transaction_code); }}
                                                placeholder="Suchen: Betrag, RG-/Auftragsnr., Kunde, Datum…"
                                                className="flex-1 border border-gray-300 rounded-sm px-1.5 py-1 text-xs bg-white min-w-0"
                                                data-testid={`sumup-recon-search-${t.transaction_code}`} />
                                            <Button type="button" size="sm" onClick={() => runSearch(t.transaction_code)}
                                                disabled={searchBusy === t.transaction_code}
                                                className="h-7 rounded-sm bg-white border border-gray-300 text-gray-800 hover:border-[#111827] text-[11px] font-bold shrink-0"
                                                data-testid={`sumup-recon-search-btn-${t.transaction_code}`}>
                                                {searchBusy === t.transaction_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <SearchIcon className="w-3 h-3 mr-1" />}
                                                Suchen
                                            </Button>
                                            <Button type="button" size="sm"
                                                onClick={() => runSearch(t.transaction_code, String(t.amount).replace(".", ","))}
                                                disabled={searchBusy === t.transaction_code}
                                                className="h-7 rounded-sm bg-white border border-gray-300 text-gray-800 hover:border-[#111827] text-[11px] font-bold shrink-0"
                                                title={`Alle Rechnungen mit Betrag ${fmtEur(t.amount)} suchen`}
                                                data-testid={`sumup-recon-search-amount-${t.transaction_code}`}>
                                                {fmtEur(t.amount)} suchen
                                            </Button>
                                        </div>
                                        {searchRes[t.transaction_code] && (
                                            <div className="mt-1.5 border border-gray-200 bg-white rounded-sm p-1.5" data-testid={`sumup-recon-results-${t.transaction_code}`}>
                                                {(searchRes[t.transaction_code].items || []).length === 0 ? (
                                                    <div className="text-[11px] text-gray-600">
                                                        Keine Rechnung zu „{searchRes[t.transaction_code].searched}" gefunden.
                                                        {(assignNo[t.transaction_code] || "").trim() && (
                                                            <button type="button" onClick={() => assignExternal(t.transaction_code)}
                                                                disabled={assigning === t.transaction_code}
                                                                className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5"
                                                                data-testid={`sumup-recon-external-${t.transaction_code}`}>
                                                                <Link2 className="w-3 h-3" /> Trotzdem als Beleg-Nr. „{(assignNo[t.transaction_code] || "").trim()}" zuordnen
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                                        {(searchRes[t.transaction_code].items || []).map((inv) => (
                                                            <div key={inv.id} className="flex items-center justify-between gap-2 text-[11px] border-b border-gray-100 last:border-0 pb-1 last:pb-0">
                                                                <div className="min-w-0 truncate">
                                                                    <span className="font-bold">{inv.invoice_number || "—"}</span>
                                                                    {inv.customer_name && <span className="text-gray-500"> · {inv.customer_name}</span>}
                                                                    <span className="text-gray-400"> · {inv.invoice_date || ""}</span>
                                                                    <span className="font-mono-num"> · {fmtEur(inv.total_gross)}</span>
                                                                    {inv.remaining > 0
                                                                        ? <span className="text-amber-700 font-bold"> · offen {fmtEur(inv.remaining)}</span>
                                                                        : <span className="text-emerald-700 font-bold"> · bezahlt</span>}
                                                                    {Math.abs(inv.remaining - t.amount) < 0.01 && <span className="text-emerald-700 font-bold"> ✓ Betrag passt</span>}
                                                                </div>
                                                                <button type="button" onClick={() => assignById(t.transaction_code, inv.id)}
                                                                    disabled={assigning === t.transaction_code}
                                                                    className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold bg-[#111827] text-white hover:bg-black rounded px-1.5 py-0.5"
                                                                    data-testid={`sumup-recon-pick-${inv.id}`}>
                                                                    <Link2 className="w-3 h-3" /> Zuordnen
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <p className="text-[10px] text-gray-400 mt-2">
                            Der nächtliche Sync (03:00) verbucht Zahlungen automatisch, wenn genau EINE offene EC-Rechnung mit passendem Betrag existiert — alles andere landet hier zur manuellen Zuordnung.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
